import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ChatbotBillingService, ServiceOption, DoctorOption, DepartmentOption, PaymentTypeOption } from '../chatbot-billing.service';
import { ChatbotAutomationService, BillingAutomationPayload, BillSavedSummary } from '../chatbot-automation.service';
import { VoiceService } from '../voice.service';
import { environment } from 'environments/environment';

function newSessionId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

type ChatViewMode = 'closed' | 'fullscreen' | 'docked' | 'floating';
type ChatState  = 'idle' | 'collecting' | 'awaiting_confirmation';

type CollectStep = 'uhid' | 'category' | 'department' | 'doctor' | 'service' | 'mode' | 'payment';

interface BillingDraft {
  uhid?: string;
  patientId?: number;
  patientName?: string;
  partyId?: number;
  doctorId?: number;
  departmentId?: number;
  departmentName?: string;
  category?: string;
  doctor?: string;
  skipDoctor?: boolean;
  service?: string;
  serviceId?: number;
  mode?: 'walkin' | 'appointment';
  paymentMethod?: string;
  paymentTypeId?: number;
  remarks?: string;
}

interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
  buttons?: BtnOption[];
}

interface BtnOption {
  label: string;
  value: string;
  icon?: string;
  cls?: string;
}

export interface TopicCard {
  label: string;
  icon: string;
  color: string;
  category: string;
  sub: string;
}

@Component({
  selector: 'app-chatbot-dialog',
  templateUrl: './chatbot-dialog.component.html',
  styleUrls: ['./chatbot-dialog.component.scss']
})
export class ChatbotDialogComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput')      messageInput!: ElementRef;
  @ViewChild('chatPanel')         chatPanel!: ElementRef;

  messages: ChatMessage[] = [];
  userInput         = '';
  inputPlaceholder  = 'Ask ELI Assistant...';
  viewMode          : ChatViewMode = 'closed';
  isListening       = false;
  micSupported      = false;
  micStatusMessage  = '';
  isTyping          = false;

  /** Floating / draggable panel position & size */
  panelTop    = 80;
  panelLeft   = 0;
  panelWidth  = 420;
  panelHeight = 680;
  isDragging  = false;
  isResizing  = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private readonly MIN_PANEL_W = 320;
  private readonly MIN_PANEL_H = 400;
  private readonly PANEL_LAYOUT_KEY = 'eli_chat_panel_layout';

  chatState    : ChatState   = 'idle';
  private draft: BillingDraft = {};
  private collectStep: CollectStep = 'uhid';
  private recognition: any;
  private pendingVoiceSubmit = false;

  // Pending service options returned by the billing API search
  private pendingServiceOptions = new Map<number, ServiceOption>();
  private pendingDoctorOptions     = new Map<number, DoctorOption>();
  private pendingDepartmentOptions = new Map<number, DepartmentOption>();
  private allDoctors: DoctorOption[] = [];
  private allDepartments: DepartmentOption[] = [];
  private doctorPage = 0;
  private deptPage   = 0;
  private readonly DOCTORS_PER_PAGE = 10;
  private readonly DEPTS_PER_PAGE   = 10;
  paymentTypes: PaymentTypeOption[] = [];

  private readonly BACKEND_URL = `${environment.chatbotApiUrl}chat`;
  private readonly HISTORY_KEY = 'eli_chat_history';
  private sessionId            = newSessionId();
  private backendAvailable     = true;

  readonly topicCards: TopicCard[] = [
    { label: 'OPD Billing',   sub: 'Out Patient', icon: 'local_hospital', color: '#2563eb', category: 'OPD' },
    { label: 'IPD Billing',   sub: 'In Patient',  icon: 'hotel',          color: '#7c3aed', category: 'IPD' },
    { label: 'Investigation', sub: 'Diagnostic',  icon: 'science',        color: '#059669', category: 'OPD Diagnostic Investigation' },
    { label: 'Laboratory',    sub: 'Lab Tests',   icon: 'biotech',        color: '#0891b2', category: 'Laboratory' },
  ];

  /** One-click demo scenarios — stream a full billing request, then auto-send. */
  readonly demoScenarios = [
    { label: 'Demo · UHID 12345', text: 'uhid 12345 OPD general consultation walk-in cash' },
    { label: 'Demo · UHID 123',   text: 'uhid 123 OPD general consultation walk-in cash' },
  ];
  private demoTimer: any = null;

  private readonly CATEGORIES: string[] = [
    'Laboratory', 'OPD', 'OPD Diagnostic Investigation', 'IPD',
    'IPD Cataract Surgical Procedures', 'IPD Minor Procedures',
    'IPD Procedures other than Cataract', 'Retinal Surgical Procedure', 'Retinal Injections',
  ];

  private readonly CATEGORY_ALIASES: { [k: string]: string } = {
    '1':'Laboratory','2':'OPD','3':'OPD Diagnostic Investigation','4':'IPD',
    '5':'IPD Cataract Surgical Procedures','6':'IPD Minor Procedures',
    '7':'IPD Procedures other than Cataract','8':'Retinal Surgical Procedure','9':'Retinal Injections',
    'lab':'Laboratory','laboratory':'Laboratory',
    'opd':'OPD','outpatient':'OPD','out patient':'OPD','general':'OPD',
    'opd diagnostic':'OPD Diagnostic Investigation','diagnostic':'OPD Diagnostic Investigation',
    'investigation':'OPD Diagnostic Investigation','opd investigation':'OPD Diagnostic Investigation',
    'ipd':'IPD','inpatient':'IPD','in patient':'IPD','admission':'IPD',
    'cataract':'IPD Cataract Surgical Procedures','minor':'IPD Minor Procedures',
    'minor procedures':'IPD Minor Procedures','ipd minor':'IPD Minor Procedures',
    'ipd procedures':'IPD Procedures other than Cataract',
    'other than cataract':'IPD Procedures other than Cataract',
    'retinal':'Retinal Surgical Procedure','retinal surgical':'Retinal Surgical Procedure',
    'retinal injection':'Retinal Injections','retinal injections':'Retinal Injections',
    'injection':'Retinal Injections',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private http: HttpClient,
    private chatbotBilling: ChatbotBillingService,
    private chatbotAutomation: ChatbotAutomationService,
    private voice: VoiceService
  ) {
    this.initSpeechRecognition();
    // Sarvam STT (server-side) works even where browser Web Speech doesn't.
    if (this.voice.supported) { this.micSupported = true; }
  }

  // ── One-click demo ──────────────────────────────────────────────────────────
  runDemo(idx: number): void {
    this.stopDemo();
    const text = this.demoScenarios[idx]?.text || '';
    if (!text) { return; }
    this.userInput = '';
    const words = text.split(' ');
    let i = 0;
    this.demoTimer = setInterval(() => {
      this.userInput = (this.userInput + ' ' + words[i]).trim();
      i++;
      this.cdr.detectChanges();
      if (i >= words.length) {
        this.stopDemo();
        setTimeout(() => this.sendMessage(), 450);
      }
    }, 85);
  }

  private stopDemo(): void {
    if (this.demoTimer) { clearInterval(this.demoTimer); this.demoTimer = null; }
  }

  ngOnInit(): void {
    this.loadHistory();
    this.loadPanelLayout();
    this.chatbotAutomation.billSaved$
      .subscribe(summary => this.onBillSaved(summary));
    this.chatbotAutomation.automationFailed$
      .subscribe(msg => this.onAutomationFailed(msg));
  }

  /** Load payment modes only after login — avoids 401 reload loop on login page. */
  private ensurePaymentTypesLoaded(): void {
    if (this.paymentTypes.length) return;
    if (!sessionStorage.getItem('token')) return;
    this.chatbotBilling.getPaymentTypes().subscribe(types => {
      this.paymentTypes = types;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.stopDemo();
    try { this.voice.cancel(); } catch { /* ignore */ }
    this.setBodyScrollLock(false);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.viewMode !== 'closed') this.closeChat();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const maxLeft = window.innerWidth - 120;
      const maxTop  = window.innerHeight - 60;
      this.panelLeft = Math.max(0, Math.min(maxLeft, event.clientX - this.dragOffsetX));
      this.panelTop  = Math.max(0, Math.min(maxTop,  event.clientY - this.dragOffsetY));
      this.cdr.detectChanges();
      return;
    }
    if (this.isResizing) {
      const maxW = window.innerWidth - this.panelLeft - 8;
      const maxH = window.innerHeight - this.panelTop - 8;
      this.panelWidth  = Math.max(this.MIN_PANEL_W, Math.min(maxW, event.clientX - this.panelLeft));
      this.panelHeight = Math.max(this.MIN_PANEL_H, Math.min(maxH, event.clientY - this.panelTop));
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.isDragging || this.isResizing) {
      this.savePanelLayout();
    }
    this.isDragging = false;
    this.isResizing = false;
  }

  get panelStyle(): { [k: string]: string } {
    if (this.viewMode === 'floating') {
      return {
        top:    this.panelTop + 'px',
        left:   this.panelLeft + 'px',
        width:  this.panelWidth + 'px',
        height: this.panelHeight + 'px',
        right:  'auto',
        bottom: 'auto',
      };
    }
    if (this.viewMode === 'docked') {
      return {
        top:    '0',
        right:  '0',
        bottom: '0',
        left:   'auto',
        width:  this.panelWidth + 'px',
        height: '100%',
      };
    }
    return {};
  }

  private savePanelLayout(): void {
    try {
      sessionStorage.setItem(this.PANEL_LAYOUT_KEY, JSON.stringify({
        panelTop: this.panelTop, panelLeft: this.panelLeft,
        panelWidth: this.panelWidth, panelHeight: this.panelHeight,
      }));
    } catch {}
  }

  private loadPanelLayout(): void {
    try {
      const raw = sessionStorage.getItem(this.PANEL_LAYOUT_KEY);
      if (!raw) return;
      const l = JSON.parse(raw);
      if (l.panelWidth  >= this.MIN_PANEL_W) this.panelWidth  = l.panelWidth;
      if (l.panelHeight >= this.MIN_PANEL_H) this.panelHeight = l.panelHeight;
      if (typeof l.panelTop  === 'number') this.panelTop  = l.panelTop;
      if (typeof l.panelLeft === 'number') this.panelLeft = l.panelLeft;
    } catch {}
  }

  onDragStart(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();

    if (this.viewMode === 'fullscreen' || this.viewMode === 'docked') {
      this._switchToFloatingAt(event.clientX - 100, event.clientY - 20);
    }

    this.isDragging  = true;
    this.dragOffsetX = event.clientX - this.panelLeft;
    this.dragOffsetY = event.clientY - this.panelTop;
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.viewMode === 'fullscreen') {
      this._switchToFloatingAt(
        window.innerWidth - this.panelWidth - 24,
        window.innerHeight - this.panelHeight - 24
      );
    } else if (this.viewMode === 'docked') {
      this._switchToFloatingAt(window.innerWidth - this.panelWidth, 0);
    }
    this.isResizing = true;
  }

  private _switchToFloatingAt(left: number, top: number): void {
    this.viewMode    = 'floating';
    this.panelLeft   = Math.max(0, left);
    this.panelTop    = Math.max(0, top);
    this.setBodyScrollLock(false);
  }

  private setBodyScrollLock(locked: boolean): void {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  private saveHistory(): void {
    try {
      sessionStorage.setItem(this.HISTORY_KEY, JSON.stringify(this.messages));
    } catch {}
  }

  private loadHistory(): void {
    try {
      const raw = sessionStorage.getItem(this.HISTORY_KEY);
      if (!raw) return;
      this.messages = JSON.parse(raw).map((m: ChatMessage) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
    } catch {}
  }

  private shouldAutoScroll = true;
  private readonly scrollThreshold = 80;

  private pushMessage(msg: ChatMessage, forceScroll = false): void {
    this.messages.push(msg);
    this.saveHistory();
    if (forceScroll) {
      this.shouldAutoScroll = true;
      setTimeout(() => this.scrollToBottom(true), 0);
    }
  }

  onMessagesScroll(): void {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.shouldAutoScroll = distanceFromBottom <= this.scrollThreshold;
  }

  scrollToBottom(force = false): void {
    if (!force && !this.shouldAutoScroll) return;
    try {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    } catch {}
  }

  // ── Topic card ────────────────────────────────────────────────────────────

  selectTopic(topic: TopicCard): void {
    this.draft       = { category: topic.category };
    this.chatState   = 'collecting';
    this.collectStep = 'uhid';
    this.sessionId   = newSessionId();
    this.pushMessage({ sender: 'user', text: topic.label, timestamp: new Date(), buttons: [] }, true);
    this.cdr.detectChanges();
    this.addBotMessage(
      `Starting ${topic.label}.\n\nPlease enter the patient UHID number:`
    );
    this.focusInput();
  }

  // ── Button click ──────────────────────────────────────────────────────────

  buttonClick(btn: BtnOption): void {
    const lower = btn.value.toLowerCase().trim();

    // ── Department selection ──────────────────────────────────────────────
    if (lower.startsWith('dept_')) {
      const id   = parseInt(btn.value.split('_')[1], 10);
      const dept = this.pendingDepartmentOptions.get(id);
      if (dept) {
        this.pushMessage({ sender: 'user', text: dept.name, timestamp: new Date(), buttons: [] }, true);
        this.cdr.detectChanges();
        this.draft.departmentId   = dept.id;
        this.draft.departmentName = dept.name;
        this.pendingDepartmentOptions.clear();
        this.collectStep = 'doctor';
        this.askForDoctor();
        this.focusInput();
        return;
      }
    }

    if (lower === 'more_departments') {
      this.pushMessage({ sender: 'user', text: btn.label, timestamp: new Date(), buttons: [] }, true);
      this.cdr.detectChanges();
      this.deptPage++;
      this.showDepartmentPicker();
      this.focusInput();
      return;
    }

    // ── Doctor selection from list ────────────────────────────────────────
    if (lower.startsWith('dr_')) {
      const id  = parseInt(btn.value.split('_')[1], 10);
      const doc = this.pendingDoctorOptions.get(id);
      if (doc) {
        this.pushMessage({ sender: 'user', text: doc.userName, timestamp: new Date(), buttons: [] }, true);
        this.cdr.detectChanges();
        this.draft.doctor     = doc.userName;
        this.draft.doctorId   = doc.userId;
        this.draft.skipDoctor = false;
        this.pendingDoctorOptions.clear();
        this.advanceToNextStep();
        this.focusInput();
        return;
      }
    }

    if (lower === 'more_doctors') {
      this.pushMessage({ sender: 'user', text: btn.label, timestamp: new Date(), buttons: [] }, true);
      this.cdr.detectChanges();
      this.doctorPage++;
      this.showDoctorPicker();
      this.focusInput();
      return;
    }

    // ── Service selection from picker ─────────────────────────────────────
    if (lower.startsWith('svc_')) {
      const id  = parseInt(btn.value.split('_')[1], 10);
      const opt = this.pendingServiceOptions.get(id);
      if (opt) {
        this.pushMessage({ sender: 'user', text: btn.label, timestamp: new Date(), buttons: [] }, true);
        this.cdr.detectChanges();
        this.draft.service   = opt.serviceName;
        this.draft.serviceId = opt.serviceId;
        this.pendingServiceOptions.clear();
        this.advanceToNextStep();
        this.focusInput();
        return;
      }
    }

    // ── Payment mode from billing API list ────────────────────────────────
    if (lower.startsWith('pay_')) {
      const id = parseInt(btn.value.split('_')[1], 10);
      const pt = this.paymentTypes.find(p => p.id === id);
      if (pt) {
        this.pushMessage({ sender: 'user', text: pt.name, timestamp: new Date(), buttons: [] }, true);
        this.cdr.detectChanges();
        this.draft.paymentMethod  = pt.name;
        this.draft.paymentTypeId  = pt.id;
        if (this.chatState === 'collecting' && this.collectStep === 'payment') {
          this.advanceToNextStep();
        }
        this.focusInput();
        return;
      }
    }

    // ── "Search again" / "More specific" ─────────────────────────────────
    if (lower === 'service_retry') {
      this.pushMessage({ sender: 'user', text: btn.label, timestamp: new Date(), buttons: [] }, true);
      this.cdr.detectChanges();
      this.pendingServiceOptions.clear();
      this.addBotMessage('Please type a more specific service name:');
      this.focusInput();
      return;
    }

    // ── Normal button click ───────────────────────────────────────────────
    this.pushMessage({ sender: 'user', text: btn.label, timestamp: new Date(), buttons: [] }, true);
    this.pendingVoiceSubmit = false;
    this.micStatusMessage   = '';
    this.cdr.detectChanges();

    switch (this.chatState) {
      case 'collecting':            this.processCurrentStep(btn.label, lower); break;
      case 'awaiting_confirmation': this.handleConfirmation(lower); break;
      default:                      this.handleIdle(lower, btn.label);
    }
    this.focusInput();
  }

  // ── Speech recognition ────────────────────────────────────────────────────

  private initSpeechRecognition(): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    this.micSupported = true;
    this.recognition  = new SR();
    this.recognition.continuous     = false;
    this.recognition.interimResults = true;
    this.recognition.language       = 'en-US';

    this.recognition.onstart  = () => { this.isListening = true;  this.micStatusMessage = 'Listening...'; this.cdr.detectChanges(); };
    this.recognition.onresult = (e: any) => {
      let t = '';
      for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript;
      this.userInput = t.toLowerCase().trim();
      this.micStatusMessage = this.userInput ? 'Speech captured.' : 'Listening...';
      this.cdr.detectChanges();
    };
    this.recognition.onend = () => {
      this.isListening      = false;
      this.micStatusMessage = this.userInput?.trim() ? 'Speech captured.' : 'Mic stopped.';
      this.cdr.detectChanges();
      if (this.pendingVoiceSubmit && this.userInput.trim()) {
        this.pendingVoiceSubmit = false;
        setTimeout(() => this.sendMessage(), 100);
      }
    };
    this.recognition.onerror = (e: any) => {
      this.isListening = false; this.pendingVoiceSubmit = false;
      this.micStatusMessage = 'Mic error: ' + (e.error || 'unknown');
      this.cdr.detectChanges();
    };
  }

  private async requestMicAccess(): Promise<boolean> {
    if (!navigator?.mediaDevices?.getUserMedia) return true;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
      return true;
    } catch {
      this.addBotMessage('Microphone permission denied. Please allow and try again.');
      return false;
    }
  }

  async toggleMic(): Promise<void> {
    // Primary path: server-side Sarvam STT (Indian languages + code-switching).
    if (this.voice.supported) {
      if (this.voice.isRecording()) {
        this.isListening = false;
        this.micStatusMessage = 'Transcribing...';
        this.cdr.detectChanges();
        try {
          const text = await this.voice.stop();
          this.userInput = (text || '').trim();
          this.micStatusMessage = this.userInput ? 'Speech captured.' : 'No speech detected.';
          this.cdr.detectChanges();
          if (this.userInput) { setTimeout(() => this.sendMessage(), 150); }
        } catch {
          this.micStatusMessage = 'Could not transcribe. Please try again.';
          this.cdr.detectChanges();
        }
        return;
      }
      this.userInput = '';
      this.micStatusMessage = 'Requesting microphone...';
      this.cdr.detectChanges();
      try {
        await this.voice.start();
        this.isListening = true;
        this.micStatusMessage = 'Listening… tap the mic to stop';
      } catch {
        this.isListening = false;
        this.micStatusMessage = 'Microphone blocked. Allow it in your browser and retry.';
      }
      this.cdr.detectChanges();
      return;
    }

    // Fallback: browser Web Speech.
    if (!this.recognition) { this.addBotMessage('Microphone not supported in your browser.'); return; }
    if (this.isListening)  { this.recognition.stop(); this.isListening = false; this.pendingVoiceSubmit = false; return; }
    this.micStatusMessage = 'Requesting microphone...';
    this.cdr.detectChanges();
    const ok = await this.requestMicAccess();
    if (!ok) return;
    this.userInput = ''; this.pendingVoiceSubmit = true;
    try { this.recognition.start(); } catch { this.addBotMessage('Mic could not start.'); }
    this.cdr.detectChanges();
  }

  onInputFocus(): void { this.inputPlaceholder = ''; }
  onInput(): void      { if (this.inputPlaceholder) this.inputPlaceholder = ''; }
  onInputBlur(): void  { if (!this.userInput?.trim()) this.inputPlaceholder = 'Ask ELI Assistant...'; }

  // ── Send message ──────────────────────────────────────────────────────────

  sendMessage(): void {
    if (!this.userInput.trim()) return;
    this.pendingVoiceSubmit = false;
    this.micStatusMessage   = '';
    const text  = this.userInput.trim();
    const lower = text.toLowerCase().trim();
    this.pushMessage({ sender: 'user', text, timestamp: new Date(), buttons: [] }, true);
    this.userInput = '';
    this.cdr.detectChanges();
    this.focusInput(30);

    if (this.chatState === 'awaiting_confirmation') { this.handleConfirmation(lower); return; }
    if (this.chatState === 'idle')                  { this.handleIdle(lower, text); return; }

    // Step-by-step OR one-shot — merge any fields from every message, then advance
    if (this.chatState === 'collecting') {
      const trimmed = text.trim();
      const lower   = trimmed.toLowerCase();

      // Service step: always search the billing catalog for what the user typed
      if (this.collectStep === 'service' && trimmed.length >= 1) {
        this.smartServiceSearch(trimmed);
        return;
      }

      // One-shot at ANY step: a message with several details -> parse it all at once
      if (this._hasMultipleBillingDetails(trimmed)) {
        this._deepseekOneShot(trimmed);
        return;
      }

      const parsed = this.parseDraft(text);
      this._mergeParsedIntoDraft(parsed, this.collectStep);

      if (this.draft.uhid && !this.draft.patientId) {
        this.validateUhidAndAdvance(this.draft.uhid, () => this._continueAfterDraftUpdate());
        return;
      }

      const newFields   = this._parsedFieldCount(parsed);
      const totalFields = this._draftFieldCount();
      const richMessage = newFields >= 2 || (totalFields >= 4 && this.collectStep !== 'service');

      if (richMessage) {
        this._continueAfterDraftUpdate();
        return;
      }

      this.processCurrentStep(text, lower);
    }
  }

  /** True when message looks like a full billing request (not a single-step answer). */
  private _isRichBillingMessage(text: string): boolean {
    const parsed = this.parseDraft(text);
    return this._parsedFieldCount(parsed) >= 2;
  }

  /** True when a message carries several billing details — route it to the one-shot
   *  DeepSeek parser even mid-flow (e.g. "1234 opd general consultation cash"). */
  private _hasMultipleBillingDetails(text: string): boolean {
    const t = (text || '').toLowerCase();
    const hasNumber = /\d{2,}|\d(\s+\d){1,}/.test(t);   // 1234 or "1 2 3 4"
    const keyword = /(opd|ipd|lab|consult|general|review|test|scan|x-?ray|biometry|surgery|procedure|injection|cash|card|upi|cheque|insurance|walk|appointment|\bdr\b|doctor)/.test(t);
    return (hasNumber && keyword) || this._isRichBillingMessage(text);
  }

  private _parsedFieldCount(d: BillingDraft): number {
    let n = 0;
    if (d.uhid)            n++;
    if (d.category)        n++;
    if (d.departmentName)  n++;
    if (d.doctor)          n++;
    if (d.service)         n++;
    if (d.mode)            n++;
    if (d.paymentMethod)   n++;
    return n;
  }

  private _draftFieldCount(): number {
    return this._parsedFieldCount(this.draft);
  }

  /** Merge parsed fields into draft without wiping existing values. */
  private _mergeParsedIntoDraft(parsed: BillingDraft, step?: CollectStep): void {
    if (parsed.uhid)            this.draft.uhid           = parsed.uhid;
    // On service step, short words like "opd" are search terms — not category changes
    if (parsed.category && step !== 'service') {
      this.draft.category = parsed.category;
    }
    if (parsed.departmentName)  this.draft.departmentName = parsed.departmentName;
    if (parsed.doctor)          { this.draft.doctor = parsed.doctor; this.draft.skipDoctor = false; }
    if (parsed.skipDoctor)      this.draft.skipDoctor     = true;
    if (parsed.service)         this.draft.service        = parsed.service;
    if (parsed.mode)            this.draft.mode           = parsed.mode;
    if (parsed.paymentMethod) {
      this.draft.paymentMethod = parsed.paymentMethod;
      if (parsed.paymentTypeId) this.draft.paymentTypeId = parsed.paymentTypeId;
    }
    if (parsed.remarks)         this.draft.remarks         = parsed.remarks;
  }

  /** Validate the answer for the current step before advancing. */
  private processCurrentStep(text: string, lower: string): void {
    switch (this.collectStep) {
      case 'uhid':
        this.validateUhidAndAdvance(text);
        break;
      case 'category':
        if (this.validateCategoryAndAdvance(lower)) {
          this.advanceToNextStep();
        }
        break;
      case 'department':
        if (this.validateDepartmentAndAdvance(text, lower)) {
          this.advanceToNextStep();
        }
        break;
      case 'doctor':
        if (this.validateDoctorAndAdvance(text, lower)) {
          this.advanceToNextStep();
        }
        break;
      case 'service':
        this.smartServiceSearch(text);
        break;
      case 'mode':
        if (this.validateModeAndAdvance(lower)) {
          this.advanceToNextStep();
        }
        break;
      case 'payment':
        if (this.validatePaymentAndAdvance(lower)) {
          this.advanceToNextStep();
        }
        break;
    }
  }

  private validateUhidAndAdvance(text: string, onValidated?: () => void): void {
    const m = text.match(/\d{4,10}/);
    if (!m) {
      this.addBotMessage('Please enter a valid UHID number (4–10 digits).');
      return;
    }

    const uhid = m[0];
    this.isTyping = true;
    this.cdr.detectChanges();

    this.chatbotBilling.lookupPatient(uhid).subscribe({
      next: (result) => {
        this.isTyping = false;
        if (!result.valid) {
          this.addBotMessage(result.error || 'Patient not found. Please enter a valid UHID.');
          this.cdr.detectChanges();
          return;
        }

        this.draft.uhid         = uhid;
        this.draft.patientId    = result.patientId;
        this.draft.patientName  = result.patientName;
        this.draft.partyId      = result.partyId;
        this.draft.doctorId     = result.doctorId;

        const idLine = result.patientId ? ` (Reg ID: ${result.patientId})` : '';

        if (onValidated) {
          this.addBotMessage(`Patient found: ${result.patientName}${idLine}.`);
          onValidated();
          this.cdr.detectChanges();
          return;
        }

        if (this._draftFieldCount() >= 3) {
          this.addBotMessage(`Patient found: ${result.patientName}${idLine}.`);
          this._continueAfterDraftUpdate();
          this.cdr.detectChanges();
          return;
        }

        if (this.draft.category) {
          this.addBotMessage(
            `Patient found: ${result.patientName}${idLine}.\n\nCategory: ${this.draft.category}. Loading departments...`
          );
          this.collectStep = 'department';
          setTimeout(() => this.askForDepartment(), 500);
        } else {
          this.addBotMessage(
            `Patient found: ${result.patientName}${idLine}.\n\nSelect billing category:`,
            this.categoryButtons()
          );
          this.collectStep = 'category';
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isTyping = false;
        this.addBotMessage('Could not verify UHID. Please try again.');
        this.cdr.detectChanges();
      }
    });
  }

  private validateCategoryAndAdvance(msg: string): boolean {
    if (msg === 'more') {
      this.addBotMessage('All categories:', this.CATEGORIES.map((c, i) => ({ label: c, value: String(i + 1) })));
      return false;
    }
    const cat = this.resolveCategory(msg);
    if (!cat) {
      this.addBotMessage('Category not recognised. Please pick one:', this.categoryButtons());
      return false;
    }
    this.draft.category = cat;
    return true;
  }

  private validateDepartmentAndAdvance(text: string, lower: string): boolean {
    const matched = this.allDepartments.find(d =>
      d.name.toLowerCase() === lower ||
      d.name.toLowerCase().includes(lower) ||
      lower.includes(d.name.toLowerCase())
    );
    if (matched) {
      this.draft.departmentId   = matched.id;
      this.draft.departmentName = matched.name;
      return true;
    }
    if (text.trim().length >= 2) {
      this.addBotMessage(
        `Department "${text.trim()}" not found. Please select from the list:`,
        this._departmentButtonsPage(0)
      );
      return false;
    }
    this.askForDepartment();
    return false;
  }

  private askForDepartment(): void {
    this.isTyping = true;
    this.cdr.detectChanges();
    this.chatbotBilling.getDepartments(0).subscribe({
      next: (depts) => {
        this.isTyping = false;
        this.allDepartments = depts;
        this.deptPage = 0;
        this.pendingDepartmentOptions.clear();
        depts.forEach(d => this.pendingDepartmentOptions.set(d.id, d));

        if (!depts.length) {
          this.addBotMessage('No departments found. Please contact admin.');
        } else {
          this.showDepartmentPicker(true);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isTyping = false;
        this.addBotMessage('Could not load departments. Please try again.');
        this.cdr.detectChanges();
      }
    });
  }

  private showDepartmentPicker(isFirst = false): void {
    const buttons = this._departmentButtonsPage(this.deptPage);
    const intro = isFirst
      ? 'Select the doctor department:'
      : 'More departments:';
    this.addBotMessage(intro, buttons);
  }

  private _departmentButtonsPage(page: number): BtnOption[] {
    const start = page * this.DEPTS_PER_PAGE;
    const slice = this.allDepartments.slice(start, start + this.DEPTS_PER_PAGE);
    const buttons: BtnOption[] = slice.map(d => ({
      label: d.name,
      value: `dept_${d.id}`,
      icon:  'domain',
    }));
    if (start + this.DEPTS_PER_PAGE < this.allDepartments.length) {
      buttons.push({ label: 'More departments...', value: 'more_departments', icon: 'expand_more' });
    }
    return buttons;
  }

  private validateDoctorAndAdvance(text: string, lower: string): boolean {
    const skip = /^(skip|default|na|none|no|s)$/i.test(lower.trim());
    if (skip) {
      this.draft.skipDoctor = true;
      this.draft.doctor     = undefined;
      this.draft.doctorId   = undefined;
      return true;
    }

    const name = text.trim().replace(/^dr\.?\s*/i, '');
    const matched = this._matchDoctorByName(name);
    if (matched) {
      this.draft.doctor     = matched.userName;
      this.draft.doctorId   = matched.userId;
      this.draft.skipDoctor = false;
      return true;
    }

    if (name.length >= 2) {
      this.addBotMessage(
        `Doctor "${name}" not found. Please select from the list:`,
        this._doctorButtonsPage(0)
      );
      return false;
    }

    this.askForDoctor();
    return false;
  }

  private _matchDoctorByName(name: string): DoctorOption | undefined {
    const q = name.toLowerCase();
    return this.allDoctors.find(d =>
      d.userName.toLowerCase() === q ||
      d.userName.toLowerCase().includes(q) ||
      q.includes(d.userName.toLowerCase())
    );
  }

  private askForDoctor(): void {
    if (!this.draft.departmentId) {
      this.collectStep = 'department';
      this.askForDepartment();
      return;
    }

    this.isTyping = true;
    this.cdr.detectChanges();
    this.chatbotBilling.getDoctors(this.draft.departmentId).subscribe({
      next: (doctors) => {
        this.isTyping = false;
        this.allDoctors = doctors;
        this.doctorPage = 0;
        this.pendingDoctorOptions.clear();
        doctors.forEach(d => this.pendingDoctorOptions.set(d.userId, d));

        if (!doctors.length) {
          this.addBotMessage(
            `No doctors found in ${this.draft.departmentName || 'this department'}.\nTap Skip to use the default.`,
            [{ label: 'Skip — use default', value: 'skip', icon: 'person_off' }]
          );
        } else {
          this.showDoctorPicker(true);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isTyping = false;
        this.addBotMessage(
          'Could not load doctor list.\nType a doctor name or tap Skip to use the default.',
          [{ label: 'Skip — use default', value: 'skip', icon: 'person_off' }]
        );
        this.cdr.detectChanges();
      }
    });
  }

  private showDoctorPicker(isFirst = false): void {
    const buttons = this._doctorButtonsPage(this.doctorPage);
    const dept    = this.draft.departmentName ? ` (${this.draft.departmentName})` : '';
    const intro   = isFirst
      ? `Select a doctor${dept}:`
      : 'More doctors:';
    this.addBotMessage(intro, buttons);
  }

  private _doctorButtonsPage(page: number): BtnOption[] {
    const start   = page * this.DOCTORS_PER_PAGE;
    const slice   = this.allDoctors.slice(start, start + this.DOCTORS_PER_PAGE);
    const buttons: BtnOption[] = slice.map(d => ({
      label: d.userName,
      value: `dr_${d.userId}`,
      icon:  'person',
    }));

    if (start + this.DOCTORS_PER_PAGE < this.allDoctors.length) {
      buttons.push({ label: 'More doctors...', value: 'more_doctors', icon: 'expand_more' });
    }
    buttons.push({ label: 'Skip — use default', value: 'skip', icon: 'person_off' });
    return buttons;
  }

  private validateModeAndAdvance(msg: string): boolean {
    if (/appt|appointment/i.test(msg)) {
      this.draft.mode = 'appointment';
      return true;
    }
    if (/walk|walkin|walk-in/i.test(msg)) {
      this.draft.mode = 'walkin';
      return true;
    }
    this.addBotMessage('Please select visit type:', [
      { label: 'Walk-in',          value: 'walkin',      icon: 'directions_walk' },
      { label: 'With Appointment', value: 'appointment', icon: 'event' },
    ]);
    return false;
  }

  private validatePaymentAndAdvance(msg: string): boolean {
    this.ensurePaymentTypesLoaded();
    const pm = this.resolvePayment(msg);
    if (!pm) {
      this.addBotMessage('Please select a payment method:', this.paymentButtonOptions());
      return false;
    }
    this.draft.paymentMethod = pm;
    return true;
  }

  private paymentButtonOptions(): BtnOption[] {
    const iconFor = (name: string): string => {
      const n = name.toLowerCase();
      if (n.includes('cash'))      return 'payments';
      if (n.includes('card'))      return 'credit_card';
      if (n.includes('upi') || n.includes('online')) return 'smartphone';
      if (n.includes('cheque') || n.includes('check')) return 'receipt_long';
      if (n.includes('insur'))     return 'health_and_safety';
      return 'account_balance_wallet';
    };
    if (this.paymentTypes.length) {
      return this.paymentTypes.map(pt => ({
        label: pt.name,
        value: `pay_${pt.id}`,
        icon:  iconFor(pt.name),
      }));
    }
    return [
      { label: 'Cash',      value: 'pay_1', icon: 'payments' },
      { label: 'Card',      value: 'pay_2', icon: 'credit_card' },
      { label: 'Online',    value: 'pay_3', icon: 'smartphone' },
      { label: 'Cheque',    value: 'pay_4', icon: 'receipt_long' },
      { label: 'Insurance', value: 'pay_7', icon: 'health_and_safety' },
    ];
  }

  private advanceToNextStep(): void {
    this._continueAfterDraftUpdate();
  }

  // ── Smart Service Picker ──────────────────────────────────────────────────

  private smartServiceSearch(rawInput: string): void {
    const query = this._expandServiceQuery(rawInput);
    this.isTyping = true;
    this.cdr.detectChanges();

    // 1. First, send to Gemini to normalize/fix typos
    const backendPayload = {
      session_id: this.sessionId,
      message:    query,
      context: { draft: this.draft, step: 'service', history: [] }
    };

    let normalizedName = query;

    const autoSelect = (r: ServiceOption) => {
      this.draft.service   = r.serviceName;
      this.draft.serviceId = r.serviceId;
      this.pendingServiceOptions.clear();
      this.addBotMessage(`Service selected: ${r.serviceName}${r.rate ? ` — ₹${r.rate}` : ''}`);
      this.advanceToNextStep();
    };

    const doSearch = (name: string) => {
      this.chatbotBilling.searchServices(name, this.draft.category).subscribe({
        next: (results) => {
          this.isTyping = false;

          if (!results || results.length === 0) {
            // No match: try without category filter OR ask to rephrase
            this.chatbotBilling.searchServices(name).subscribe({
              next: (broad) => {
                if (!broad?.length) {
                  this.addBotMessage(
                    `No services found matching "${rawInput}".\n\nTry a shorter word (e.g. "consult", "scan", "blood") or tap the button below.`,
                    [{ label: 'Try again', value: 'service_retry', icon: 'search' }]
                  );
                } else if (broad.length === 1) {
                  autoSelect(broad[0]);
                } else {
                  this.showServicePicker(name, broad);
                }
              },
              error: () => {
                this.isTyping = false;
                this.addBotMessage(
                  `No services found for "${rawInput}". Please try a different name:`,
                  [{ label: 'Try again', value: 'service_retry', icon: 'search' }]
                );
              }
            });
            return;
          }

          // Exactly 1 result — auto-select without showing a picker
          if (results.length === 1) {
            autoSelect(results[0]);
            return;
          }

          this.showServicePicker(name, results, normalizedName !== rawInput ? normalizedName : undefined);
        },
        error: () => {
          this.isTyping = false;
          this.draft.service = rawInput.replace(/\b\w/g, l => l.toUpperCase());
          this.advanceToNextStep();
        }
      });
    };

    // Try Gemini normalization first, then search (skip backend if unavailable)
    if (this.backendAvailable) {
      this.http.post<any>(this.BACKEND_URL, backendPayload).subscribe({
        next: (res) => {
          const extracted = res?.draft_update?.service || res?.extracted?.service;
          normalizedName  = extracted || query;
          doSearch(normalizedName);
        },
        error: () => {
          this.backendAvailable = false;
          doSearch(query);
        }
      });
    } else {
      doSearch(query);
    }
  }

  /** Expand common shorthand service search terms. */
  private _expandServiceQuery(raw: string): string {
    const q = raw.trim().toLowerCase();
    const aliases: Record<string, string> = {
      cons: 'consultation',
      consult: 'consultation',
      'opd consul': 'opd consultation',
      'opd cons': 'opd consultation',
      opdcons: 'opd consultation',
      scan: 'scan',
      xray: 'x-ray',
      'x ray': 'x-ray',
    };
    return aliases[q] || raw.trim();
  }

  private showServicePicker(query: string, results: ServiceOption[], correctedName?: string): void {
    this.pendingServiceOptions.clear();
    results.forEach(r => this.pendingServiceOptions.set(r.serviceId, r));

    // Build buttons: label = name + rate, value = svc_{id}
    const buttons: BtnOption[] = results.map(r => ({
      label: r.serviceName + (r.rate ? `  —  ₹${r.rate}` : '') + (r.categoryName ? `  (${r.categoryName})` : ''),
      value: `svc_${r.serviceId}`,
      icon:  'medical_services',
    }));

    // If >7 results, add "More specific" button
    if (results.length >= 7) {
      buttons.push({ label: 'More specific...', value: 'service_retry', icon: 'search' });
    }

    let intro = `Found ${results.length} service${results.length > 1 ? 's' : ''} matching "${query}". Select one:`;
    if (correctedName && correctedName !== query) {
      intro = `Auto-corrected to "${correctedName}". Found ${results.length} matching service${results.length > 1 ? 's' : ''}:`;
    }

    this.addBotMessage(intro, buttons);
  }

  // ── Gemini backend NLU (for non-service steps) ────────────────────────────

  private sendToBackend(text: string): void {
    this.isTyping = true;
    this.cdr.detectChanges();

    const payload = {
      session_id: this.sessionId,
      message:    text,
      context: {
        draft:   this.draft,
        step:    this.collectStep,
        history: this.messages.slice(-10).map(m => ({
          role: m.sender === 'user' ? 'user' : 'bot',
          text: m.text
        }))
      }
    };

    this.http.post<any>(this.BACKEND_URL, payload).subscribe({
      next: (res) => {
        this.isTyping = false;
        if (res.draft_update) this.draft = Object.assign({}, this.draft, res.draft_update);

        // If service was extracted at a non-service step (full sentence mid-conversation),
        // redirect to catalog search instead of storing the raw Gemini name
        const extractedService = res.draft_update?.service as string | undefined;
        if (extractedService && this.collectStep !== 'service') {
          this.draft.service   = undefined;
          this.draft.serviceId = undefined;
          this.collectStep     = 'service';
          // Show the AI response text first, then trigger search
          if (res.response_text) {
            this.messages.push({ sender: 'bot', text: res.response_text, timestamp: new Date(), buttons: [] });
          }
          this.scrollToBottom();
          this.cdr.detectChanges();
          this.smartServiceSearch(extractedService);
          return;
        }

        if (res.next_step)    this.collectStep = res.next_step as CollectStep;
        if (res.is_review)    this.chatState = 'awaiting_confirmation';

        const buttons = (res.buttons || []).map((b: any) => ({ label: b.label, value: b.value, icon: b.icon, cls: b.cls }));
        if (res.is_review && !buttons.length) {
          buttons.push(
            { label: 'Yes, Save Bill', value: 'yes', icon: 'check_circle', cls: 'confirm' },
            { label: 'Cancel',         value: 'no',  icon: 'cancel',       cls: 'danger'  }
          );
        }
        this.messages.push({ sender: 'bot', text: res.response_text || '', timestamp: new Date(), buttons });
        this.scrollToBottom();
        this.cdr.detectChanges();
      },
      error: () => {
        this.isTyping = false;
        this.backendAvailable = false;
        this.handleCollecting(text.toLowerCase().trim());
        this.cdr.detectChanges();
      }
    });
  }

  // ── IDLE ──────────────────────────────────────────────────────────────────

  private handleIdle(msg: string, rawText: string): void {
    const hasBillingWord = /\b(billing|bill|create|generate|uhid|patient)\b/i.test(msg);
    const isBareUhid     = /^\d{4,10}$/.test(msg.trim());
    const parsed         = this.parseDraft(rawText);
    const fieldCount     = this._parsedFieldCount(parsed);

    if (!hasBillingWord && !isBareUhid && !parsed.uhid) {
      this.addBotMessage(
        'I help you create patient bills.\n\n' +
        '• Step by step: type a UHID or pick a category above\n' +
        '• All in one message, e.g.:\n' +
        '  "uhid 1345 OPD consultation walkin cash"\n' +
        '  "bill 1345 opd dr sharma blood test cash"\n' +
        '• Or just: "generate bill 1345"'
      );
      return;
    }

    this.chatState = 'collecting';
    this.sessionId = newSessionId();

    if (isBareUhid) {
      this.draft = {};
      this.validateUhidAndAdvance(msg.trim());
      return;
    }

    // Single-shot: let DeepSeek extract & map everything, then ONE confirmation
    void fieldCount;
    this._deepseekOneShot(rawText);
  }

  /** DeepSeek one-shot: free text -> all fields (category/service mapped to the DB
   *  lists) -> resolve ids -> single review. Falls back to regex parse on error. */
  private _deepseekOneShot(rawText: string): void {
    this.addBotMessage('Got it — reading your full request…');
    const payTypes = (this.paymentTypes || []).map(p => p.name);
    this.http.post<any>(`${environment.chatbotApiUrl}billing/parse`, { message: rawText, paymentTypes: payTypes })
      .subscribe({
        next: (res: any) => {
          if (res && res.ok && (res.uhid || res.category || res.service)) {
            const mode = res.mode === 'appointment' ? 'appointment'
              : (res.mode === 'walkin' ? 'walkin' : undefined);
            this.draft = {
              uhid: res.uhid || undefined,
              category: res.category || undefined,
              service: res.service || undefined,
              doctor: res.doctor || undefined,
              mode: mode as any,
              paymentMethod: res.paymentMethod || undefined,
              remarks: res.remarks || undefined,
            };
            this._continueAfterDraftUpdate();
          } else {
            this.draft = this.parseDraft(rawText);
            this._continueAfterDraftUpdate();
          }
        },
        error: () => { this.draft = this.parseDraft(rawText); this._continueAfterDraftUpdate(); },
      });
  }

  /** After one-shot parse: resolve department/doctor, then ask only for missing fields. */
  private processOneShotRequest(rawText: string): void {
    this.draft = this.parseDraft(rawText);
    this.addBotMessage('Got it! Processing your full billing request...');

    if (this.draft.uhid) {
      this.validateUhidAndAdvance(this.draft.uhid, () => this._continueAfterDraftUpdate());
    } else {
      this.collectStep = 'uhid';
      this.askFor('uhid');
    }
  }

  /** Resolve names/IDs, then show review or ask only what is still missing. */
  private _continueAfterDraftUpdate(): void {
    const done = () => {
      if (!this.draft.mode && this.draft.uhid && this.draft.category) {
        this.draft.mode = 'walkin';
      }
      if (!this.draft.doctor && !this.draft.skipDoctor && !this.draft.doctorId) {
        if (this.draft.category && this.draft.service && this.draft.paymentMethod && this.draft.mode) {
          this.draft.skipDoctor = true;
        }
      }
      if (this.draft.service && !this.draft.serviceId) {
        this.collectStep = 'service';
        this.smartServiceSearch(this.draft.service!);
        return;
      }
      const missing = this.firstMissing();
      if (missing) {
        this.collectStep = missing;
        this.askFor(missing);
      } else {
        this.showReview();
      }
    };

    if (this.draft.uhid && !this.draft.patientId) {
      this.validateUhidAndAdvance(this.draft.uhid, () => this._continueAfterDraftUpdate());
      return;
    }

    if (this.draft.departmentName && !this.draft.departmentId) {
      this._resolveDepartmentByName(this.draft.departmentName, () => {
        if (this.draft.doctor && !this.draft.skipDoctor && !this.draft.doctorId) {
          this._resolveDoctorByName(this.draft.doctor!, done);
        } else {
          done();
        }
      });
      return;
    }

    if (this.draft.doctor && !this.draft.skipDoctor && !this.draft.doctorId) {
      if (this.draft.departmentId) {
        this._resolveDoctorByName(this.draft.doctor, done);
      } else {
        this._resolveDoctorGlobally(this.draft.doctor, done);
      }
      return;
    }

    done();
  }

  /** @deprecated use _continueAfterDraftUpdate */
  private continueOneShotFlow(): void {
    this._continueAfterDraftUpdate();
  }

  private _resolveDepartmentByName(name: string, onDone: () => void): void {
    this.chatbotBilling.getDepartments(0).subscribe(depts => {
      const q = name.toLowerCase();
      const matched = depts.find(d =>
        d.name.toLowerCase() === q ||
        d.name.toLowerCase().includes(q) ||
        q.includes(d.name.toLowerCase())
      );
      if (matched) {
        this.draft.departmentId   = matched.id;
        this.draft.departmentName = matched.name;
        this.allDepartments = depts;
        depts.forEach(d => this.pendingDepartmentOptions.set(d.id, d));
      }
      onDone();
    });
  }

  private _resolveDoctorByName(name: string, onDone: () => void): void {
    if (!this.draft.departmentId) {
      this._resolveDoctorGlobally(name, onDone);
      return;
    }
    this.chatbotBilling.getDoctors(this.draft.departmentId).subscribe(doctors => {
      this.allDoctors = doctors;
      doctors.forEach(d => this.pendingDoctorOptions.set(d.userId, d));
      const matched = this._matchDoctorByName(name);
      if (matched) {
        this.draft.doctor   = matched.userName;
        this.draft.doctorId = matched.userId;
        this.draft.skipDoctor = false;
      }
      onDone();
    });
  }

  /** Find doctor across departments when department was not specified. */
  private _resolveDoctorGlobally(name: string, onDone: () => void): void {
    const q = name.trim().replace(/^dr\.?\s*/i, '').toLowerCase();
    if (!q) {
      onDone();
      return;
    }

    const tryDepartments = (depts: DepartmentOption[]) => {
      this.allDepartments = depts;
      depts.forEach(d => this.pendingDepartmentOptions.set(d.id, d));
      let idx = 0;
      const next = () => {
        if (idx >= depts.length) {
          onDone();
          return;
        }
        const dept = depts[idx++];
        this.chatbotBilling.getDoctors(dept.id).subscribe({
          next: (doctors) => {
            const matched = doctors.find(d =>
              d.userName.toLowerCase() === q ||
              d.userName.toLowerCase().includes(q) ||
              q.includes(d.userName.toLowerCase())
            );
            if (matched) {
              this.draft.departmentId   = dept.id;
              this.draft.departmentName   = dept.name;
              this.draft.doctor           = matched.userName;
              this.draft.doctorId         = matched.userId;
              this.draft.skipDoctor       = false;
              this.allDoctors             = doctors;
              doctors.forEach(d => this.pendingDoctorOptions.set(d.userId, d));
              onDone();
            } else {
              next();
            }
          },
          error: () => next(),
        });
      };
      next();
    };

    if (this.allDepartments.length) {
      tryDepartments(this.allDepartments);
    } else {
      this.chatbotBilling.getDepartments(0).subscribe({
        next: (depts) => tryDepartments(depts || []),
        error: () => onDone(),
      });
    }
  }

  // ── Full one-shot billing request ─────────────────────────────────────────

  private processFullBillingRequest(msg: string): void {
    this.isTyping = true;
    this.cdr.detectChanges();

    const payload = {
      session_id: this.sessionId,
      message:    msg,
      context:    { draft: {}, step: 'uhid', history: [] }
    };

    this.http.post<any>(this.BACKEND_URL, payload).subscribe({
      next: (res) => {
        this.isTyping = false;
        if (res.draft_update) {
          this.draft = Object.assign({}, this.draft, res.draft_update);
        }

        // Pull service out of draft — catalog search will confirm the actual service
        const extractedService = res.draft_update?.service as string | undefined;
        if (extractedService) {
          this.draft.service   = undefined;
          this.draft.serviceId = undefined;
          this.collectStep     = 'service';
          this.smartServiceSearch(extractedService);
        } else {
          const missing = this.firstMissing();
          if (missing) { this.collectStep = missing; this.askFor(missing); }
          else         { this.showReview(); }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isTyping = false;
        this.backendAvailable = false;
        this.draft = this.parseDraft(msg);
        const missing = this.firstMissing();
        if (missing) { this.collectStep = missing; this.askFor(missing); }
        else         { this.showReview(); }
        this.cdr.detectChanges();
      }
    });
  }

  // ── COLLECTING (local fallback / button values) ───────────────────────────

  private handleCollecting(msg: string): void {
    if (this.collectStep === 'service' && msg.trim().length >= 1) {
      this.smartServiceSearch(msg.trim());
      return;
    }

    const parsed = this.parseDraft(msg);
    this._mergeParsedIntoDraft(parsed, this.collectStep);

    if (/\b(billing|bill)\b/i.test(msg) && /\d{4,10}/.test(msg)) {
      const fresh = this.parseDraft(msg);
      this._mergeParsedIntoDraft(fresh, this.collectStep);
    } else {
      const ok = this.fillField(this.collectStep, msg);
      if (!ok && this._parsedFieldCount(parsed) < 2) return;
    }

    if (this.draft.uhid && !this.draft.patientId) {
      this.validateUhidAndAdvance(this.draft.uhid, () => this._continueAfterDraftUpdate());
      return;
    }

    this._continueAfterDraftUpdate();
  }

  private fillField(step: CollectStep, msg: string): boolean {
    switch (step) {
      case 'uhid': {
        const m = msg.match(/\d{4,10}/);
        if (m) { this.draft.uhid = m[0]; return true; }
        this.addBotMessage('Please enter a valid UHID number (4–10 digits).');
        return false;
      }
      case 'category': {
        if (msg === 'more') {
          this.addBotMessage('All categories:', this.CATEGORIES.map((c, i) => ({ label: c, value: String(i + 1) })));
          return false;
        }
        const cat = this.resolveCategory(msg);
        if (cat) { this.draft.category = cat; return true; }
        this.addBotMessage('Category not recognised. Pick one:', this.categoryButtons());
        return false;
      }
      case 'doctor': {
        const skip = /^(skip|default|na|none|no|s)$/i.test(msg.trim());
        if (skip) { this.draft.skipDoctor = true; this.draft.doctor = undefined; }
        else {
          this.draft.doctor     = msg.trim().replace(/^dr\.?\s*/i, '').replace(/\b\w/g, l => l.toUpperCase());
          this.draft.skipDoctor = false;
        }
        return true;
      }
      case 'service': {
        // This path is only reached via local fallback; normally handled by smartServiceSearch
        const raw = msg.trim();
        if (!raw) { this.addBotMessage('Please enter a service or procedure name.'); return false; }
        this.draft.service = raw.replace(/\b\w/g, l => l.toUpperCase());
        return true;
      }
      case 'mode': {
        this.draft.mode = /appt|appointment/i.test(msg) ? 'appointment' : 'walkin';
        return true;
      }
      case 'payment': {
        this.draft.paymentMethod = this.resolvePayment(msg);
        return true;
      }
      default: return true;
    }
  }

  private askFor(step: CollectStep): void {
    switch (step) {
      case 'uhid':
        this.addBotMessage('Please enter the patient UHID number:');
        break;
      case 'category':
        this.addBotMessage('Select billing category:', this.categoryButtons());
        break;
      case 'department':
        this.askForDepartment();
        break;
      case 'doctor':
        this.askForDoctor();
        break;
      case 'service':
        this.addBotMessage(
          'Enter the service / procedure name' +
          (this.draft.category ? ` for ${this.draft.category}` : '') + ':' +
          '\nI will search the billing catalog and show you matching options.'
        );
        break;
      case 'mode':
        this.addBotMessage('Select visit type:', [
          { label: 'Walk-in',          value: 'walkin',      icon: 'directions_walk' },
          { label: 'With Appointment', value: 'appointment', icon: 'event' },
        ]);
        break;
      case 'payment':
        this.ensurePaymentTypesLoaded();
        this.addBotMessage('Select payment method:', this.paymentButtonOptions());
        break;
    }
  }

  private firstMissing(): CollectStep | null {
    if (!this.draft.uhid || !this.draft.patientId)         return 'uhid';
    if (!this.draft.category)                                return 'category';
    const needsDoctor = !this.draft.skipDoctor;
    if (needsDoctor && !this.draft.doctor && !this.draft.doctorId) return 'doctor';
    if (!this.draft.service)                                 return 'service';
    if (!this.draft.mode)                                    return 'mode';
    if (!this.draft.paymentMethod)                           return 'payment';
    return null;
  }

  // ── REVIEW ────────────────────────────────────────────────────────────────

  private showReview(): void {
    this.chatState     = 'awaiting_confirmation';
    const d            = this.draft;
    const billSeries   = this.getBillSeries(d.category);
    const entryType    = this.getEntryType(d.category);
    const doctorLine   = d.doctor
      ? `Doctor      : ${this.formatDoctorDisplay(d.doctor)}`
      : `Doctor      : (use billing config default)`;
    const deptLine     = d.departmentName ? `Department  : ${d.departmentName}\n` : '';
    const serviceLine  = d.service + (d.serviceId ? ` [ID:${d.serviceId}]` : '');

    const text =
      `Billing Summary\n\n` +
      `Patient     : ${d.patientName || '-'} (UHID: ${d.uhid})\n` +
      `Reg ID      : ${d.patientId || '-'}\n` +
      `Bill Series : ${billSeries}\n` +
      `Entry Type  : ${entryType}\n` +
      `Category    : ${d.category}\n` +
      deptLine +
      `${doctorLine}\n` +
      `Service     : ${serviceLine}\n` +
      `Visit Type  : ${d.mode === 'appointment' ? 'With Appointment' : 'Walk-in'}\n` +
      `Payment     : ${d.paymentMethod}` +
      (d.remarks ? `\nRemarks     : ${d.remarks}` : '') +
      `\n\nConfirm and save the bill?`;

    this.addBotMessage(text, [
      { label: 'Yes, Save Bill', value: 'yes', icon: 'check_circle', cls: 'confirm' },
      { label: 'Cancel',         value: 'no',  icon: 'cancel',       cls: 'danger'  },
    ]);
  }

  private handleConfirmation(msg: string): void {
    if (this.isYes(msg))      { this.navigateToBilling(); }
    else if (this.isNo(msg))  { this.resetSession(); this.addBotMessage('Cancelled. Select a category or type UHID to start again.'); }
    else                      { this.addBotMessage('Please click Yes or No above.'); }
  }

  // ── NAVIGATE ──────────────────────────────────────────────────────────────

  private navigateToBilling(): void {
    // Pre-open print window on user click so auto-print works after async bill save.
    this.chatbotAutomation.reservePrintWindow();

    const d = this.draft;
    const payload: BillingAutomationPayload = {
      uhid:          d.uhid || null,
      patientId:     d.patientId || null,
      patientName:   d.patientName || null,
      category:      d.category || null,
      doctor:        d.doctor || null,
      doctorId:      d.doctorId || null,
      departmentId:  d.departmentId || null,
      departmentName:d.departmentName || null,
      skipDoctor:    d.skipDoctor || false,
      services:      d.service ? [d.service] : [],
      service:       d.service || null,
      serviceId:     d.serviceId || null,
      mode:          d.mode || null,
      paymentMethod: d.paymentMethod || null,
      paymentTypeId: d.paymentTypeId || null,
      cashPaid:      (d.paymentTypeId === 1) || (d.paymentMethod || '').toLowerCase() === 'cash',
      remarks:       d.remarks || null,
      autoSubmit:    true,
      autoPrint:     true,
      autoReceipt:   true,
    };

    const inPlace = this.chatbotAutomation.triggerBillingAutomation(payload);

    this.addBotMessage(
      inPlace
        ? `Applying bill on the billing page...\n\n` +
          `Patient: ${d.patientName || d.uhid} (UHID: ${d.uhid})\n` +
          `Category: ${d.category}\nService: ${d.service}\nPayment: ${d.paymentMethod}\n\n` +
          `I'm staying open while billing is processed.`
        : `Opening billing page...\n\n` +
          `Patient: ${d.patientName || d.uhid} (UHID: ${d.uhid})\n` +
          `Category: ${d.category}\nService: ${d.service}\nPayment: ${d.paymentMethod}\n\n` +
          `I'm staying open while billing is processed.`
    );

    this.resetFlow();

    if (inPlace) {
      this.dockChat();
    } else {
      setTimeout(() => {
        this.router.navigate(['/layout/Billing'], { state: { billingAutomation: payload } })
          .then(() => this.dockChat());
      }, 400);
    }
  }

  private onBillSaved(summary: BillSavedSummary): void {
    const serviceLines = summary.services?.length
      ? summary.services.map(s => `  • ${s}`).join('\n')
      : '  • —';

    const text =
      `✅ Bill saved successfully!\n\n` +
      `Bill ID     : ${summary.billId}${summary.billNo ? `\nBill No     : ${summary.billNo}` : ''}\n` +
      `Patient     : ${summary.patientName} (UHID: ${summary.uhid})\n` +
      `Category    : ${summary.category}\n` +
      (summary.department ? `Department  : ${summary.department}\n` : '') +
      `Doctor      : ${summary.doctor}\n` +
      `Services    :\n${serviceLines}\n` +
      `Visit Type  : ${summary.mode}\n` +
      `Payment     : ${summary.paymentMethod}\n` +
      (summary.remarks ? `Remarks     : ${summary.remarks}\n` : '') +
      (summary.netAmount != null ? `Net Amount  : ₹${summary.netAmount}\n` : '') +
      (summary.paidAmount != null ? `Paid Amount : ₹${summary.paidAmount}\n` : '') +
      (summary.receiptCreated
        ? `\n💳 Receipt recorded (${summary.paymentMethod}).`
        : (summary.paymentMethod && summary.paymentMethod.toLowerCase() !== 'cash'
          ? `\nBill saved. Receipt for ${summary.paymentMethod} may need manual entry on billing page.`
          : '')) +
      (summary.printed
        ? `\n🖨 Invoice print has been sent to the printer.`
        : `\nBill saved. Print was not triggered automatically.`);

    this.chatState = 'idle';
    this.resetFlow();
    this.dockChat();
    this.addBotMessage(text);
  }

  private onAutomationFailed(message: string): void {
    this.addBotMessage(`⚠️ Bill could not be saved automatically.\n\n${message}\n\nPlease complete or save on the billing page.`);
  }

  private formatDoctorDisplay(name: string): string {
    const cleaned = name.replace(/^(dr\.?\s*)+/i, '').trim();
    return cleaned ? `Dr. ${cleaned}` : name;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private categoryButtons(): BtnOption[] {
    return [
      { label: 'OPD',           value: 'opd',           icon: 'local_hospital' },
      { label: 'IPD',           value: 'ipd',           icon: 'hotel' },
      { label: 'Investigation', value: 'investigation',  icon: 'science' },
      { label: 'Laboratory',    value: 'lab',            icon: 'biotech' },
      { label: 'More...',       value: 'more',           icon: 'expand_more' },
    ];
  }

  private getBillSeries(category?: string): string {
    if (!category) return '-';
    const c = category.toLowerCase();
    if (c === 'laboratory' || c.includes('investigation') || c.includes('diagnostic')) return 'Investigation Billing';
    if (c.startsWith('ipd') || c.includes('retinal') || c.includes('cataract') || c.includes('minor') || c.includes('procedures')) return 'IPD Billing';
    return 'OPD Billing';
  }

  private getEntryType(category?: string): string {
    const s = this.getBillSeries(category);
    if (s === 'IPD Billing')           return 'In Patient';
    if (s === 'Investigation Billing') return 'Investigations';
    return 'Out Patient';
  }

  private parseDraft(input: string): BillingDraft {
    const draft: BillingDraft = {};
    this._parseStructuredSummary(input, draft);

    // UHID: explicit label OR any 4–10 digit number in a billing context
    if (!draft.uhid) {
      const uhidM = input.match(/(?:uhid|uuid|uid|patient\s*id|reg\s*id)\s*[:=\s]?\s*(\d{4,10})/i)
                 || input.match(/\bbill(?:ing)?\s+(?:for\s+)?(\d{4,10})\b/i)
                 || input.match(/\bgenerate\s+(?:the\s+)?bill\s+(?:for\s+)?(\d{4,10})\b/i)
                 || input.match(/\b(\d{4,10})\b/);
      if (uhidM) draft.uhid = uhidM[1];
    }

    if (!draft.category) {
      draft.category = this.extractCategory(input);
    }

    if (!draft.departmentName) {
      const deptM = input.match(/(?:department|dept|speciality|specialty)\s*:\s*([a-zA-Z][a-zA-Z\s/]+?)(?=\s+(?:doctor|dr\.?|service|visit|payment|walkin|cash|card|$))/i)
               || input.match(/(?:department|dept|speciality|specialty)\s+([a-zA-Z][a-zA-Z\s/]+?)(?=\s+(?:doctor|dr\.?|service|walkin|cash|card|$))/i);
      if (deptM) draft.departmentName = deptM[1].trim().replace(/\b\w/g, l => l.toUpperCase());
    }

    if (!draft.doctor && !draft.skipDoctor) {
      const drLabelM = input.match(/doctor\s*:\s*(?:dr\.?\s*)?(.+?)(?=\s+(?:service|visit\s*type|payment|department|category|reg\s*id|$))/i);
      const drM = drLabelM
        || input.match(/(?:dr\.?|doctor|doc)\.?\s+([A-Za-z]+(?:\s+[A-Za-z]+)*)/i);
      if (drM) {
        draft.doctor     = drM[1].trim().replace(/^dr\.?\s*/i, '').replace(/\b\w/g, l => l.toUpperCase());
        draft.skipDoctor = false;
      } else if (/\b(skip\s*doctor|no\s*doctor)\b/i.test(input)) {
        draft.skipDoctor = true;
      }
    }

    if (!draft.mode) {
      if (/\b(appointment|appt)\b/i.test(input))              draft.mode = 'appointment';
      else if (/\b(walkin|walk-in|walk\s+in)\b/i.test(input)) draft.mode = 'walkin';
      else if (draft.uhid && draft.category)                  draft.mode = 'walkin';
    }

    if (!draft.paymentMethod) {
      const payLabelM = input.match(/payment\s*:\s*(\w+)/i);
      if (payLabelM) {
        draft.paymentMethod = this.resolvePayment(payLabelM[1], draft);
      } else {
        const payM = input.match(/\b(cash|card|online|upi|cheque|insurance)\b/i);
        if (payM) draft.paymentMethod = this.resolvePayment(payM[1], draft);
      }
    }

    if (!draft.service) {
      // Service: keyword, after doctor name, or common procedure names
      let svcM = input.match(/\bservice\s*:\s*(.+?)(?=\s+(?:visit\s*type|payment|remarks|reg\s*id|department|doctor|\[ID|$))/i)
              || input.match(/\bservice\s+(?:name\s+)?([a-zA-Z][a-zA-Z0-9\s/+-]+?)(?=\s+(?:walkin|walk[\-\s]in|cash|card|online|cheque|insurance|appointment|appt|remarks?|uhid)|$)/i);
      if (!svcM && draft.doctor) {
        const drPat = new RegExp(
          `(?:dr\\.?|doctor|doc)\\.?\\s+${draft.doctor.replace(/\s+/g, '\\s+')}`,
          'i'
        );
        const drMatch = input.match(drPat);
        if (drMatch) {
          const afterDr = input.slice(input.indexOf(drMatch[0]) + drMatch[0].length);
          svcM = afterDr.match(/^\s+([a-zA-Z][a-zA-Z0-9\s/()+-]{2,60}?)(?=\s+(?:walkin|walk[\-\s]in|cash|card|online|cheque|insurance|appointment|appt|remarks?|\[ID)|$)/i);
        }
      }
      if (!svcM) {
        svcM = input.match(/\b(?:for|add)\s+([a-zA-Z][a-zA-Z0-9\s/+-]{2,40}?)(?=\s+(?:walkin|walk[\-\s]in|cash|card|online|cheque|insurance|appointment|appt|remarks?)|$)/i);
      }
      if (!svcM) {
        const commonSvc = input.match(
          /\b(consultation|consult|follow[\s-]?up|review|checkup|check[\s-]?up|blood test|cbc|x[\s-]?ray|ultrasound|ecg|echo|mri|ct scan|dressing|injection|vaccination|a scan)\b/i
        );
        if (commonSvc) svcM = commonSvc;
      }
      if (svcM) {
        let svc = svcM[1].trim();
        const idM = svc.match(/\[ID\s*:\s*(\d+)\]/i);
        if (idM) {
          draft.serviceId = parseInt(idM[1], 10);
          svc = svc.replace(/\s*\[ID\s*:\s*\d+\]/gi, '').trim();
        }
        if (!/^(opd|ipd|lab|laboratory|investigation|department|dept)$/i.test(svc)) {
          draft.service = svc.replace(/\b\w/g, l => l.toUpperCase());
        }
      }
    }

    const remM = input.match(/\bremarks?\s+(.+?)(?:\s*$)/i);
    if (remM) draft.remarks = remM[1].trim();

    return draft;
  }

  /**
   * Parse pasted billing summary blocks, e.g.
   * "Patient : X (UHID: 1345) ... Service : A SCAN(Both Eye) [ID:1] ... Payment : Cash"
   */
  private _parseStructuredSummary(input: string, draft: BillingDraft): boolean {
    if (!/\b(category|department|doctor|service|visit\s*type|payment)\s*:/i.test(input)) {
      return false;
    }

    const nextLabel =
      '(?=\\s+(?:Patient|Reg\\s*ID|Bill\\s*Series|Entry\\s*Type|Category|Department|Doctor|Service|Visit\\s*Type|Payment|Remarks)\\s*:|$)';

    const labelValue = (label: string): string | undefined => {
      const re = new RegExp(`\\b${label}\\s*:\\s*(.+?)${nextLabel}`, 'i');
      const m  = input.match(re);
      return m ? m[1].trim() : undefined;
    };

    const uhidParen = input.match(/\(UHID\s*:\s*(\d{4,10})\)/i);
    const regId     = input.match(/\bReg\s*ID\s*:\s*(\d{4,10})/i);
    if (uhidParen) draft.uhid = uhidParen[1];
    else if (regId) draft.uhid = regId[1];

    const patientRaw = labelValue('Patient');
    if (patientRaw && !draft.patientName) {
      draft.patientName = patientRaw.replace(/\s*\(UHID\s*:\s*\d+\)/i, '').trim();
    }

    const cat = labelValue('Category');
    if (cat) {
      draft.category = this.resolveCategory(cat.trim()) || cat.trim();
    }

    const dept = labelValue('Department');
    if (dept) {
      draft.departmentName = dept.replace(/\b\w/g, l => l.toUpperCase());
    }

    const drRaw = labelValue('Doctor');
    if (drRaw) {
      draft.doctor = drRaw
        .replace(/^dr\.?\s*/i, '')
        .trim()
        .replace(/\b\w/g, l => l.toUpperCase());
      draft.skipDoctor = false;
    }

    const svcRaw = labelValue('Service');
    if (svcRaw) {
      let svc = svcRaw.trim();
      const idM = svc.match(/\[ID\s*:\s*(\d+)\]/i);
      if (idM) {
        draft.serviceId = parseInt(idM[1], 10);
        svc = svc.replace(/\s*\[ID\s*:\s*\d+\]/gi, '').trim();
      }
      draft.service = svc.replace(/\b\w/g, l => l.toUpperCase());
    }

    const visit = labelValue('Visit\\s*Type');
    if (visit) {
      draft.mode = /appt|appointment/i.test(visit) ? 'appointment' : 'walkin';
    }

    const pay = labelValue('Payment');
    if (pay) {
      const resolved = this.resolvePayment(pay.split(/\s+/)[0], draft);
      if (resolved) draft.paymentMethod = resolved;
    }

    return true;
  }

  private extractCategory(input: string): string | undefined {
    const lower = input.toLowerCase();
    const kwM   = lower.match(/(?:category|cat|dept)\s*[:=]?\s*([\w\s]+?)(?=\s+(?:service|walkin|cash|card|appointment|uhid|\d)|$)/i);
    if (kwM) { const r = this.resolveCategory(kwM[1].trim()); if (r) return r; }
    const keys = Object.keys(this.CATEGORY_ALIASES).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      if (/^\d+$/.test(key)) continue;
      if (lower.includes(key)) return this.CATEGORY_ALIASES[key];
    }
    return undefined;
  }

  private resolveCategory(raw: string): string | undefined {
    const lower = raw.toLowerCase().trim();
    if (this.CATEGORY_ALIASES[lower]) return this.CATEGORY_ALIASES[lower];
    const keys = Object.keys(this.CATEGORY_ALIASES).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      if (lower.includes(key) || key.includes(lower)) return this.CATEGORY_ALIASES[key];
    }
    return this.CATEGORIES.find(c => c.toLowerCase() === lower);
  }

  private resolvePayment(raw: string, target?: BillingDraft): string | null {
    const d = target || this.draft;
    const r = raw.toLowerCase().trim();

    for (const pt of this.paymentTypes) {
      const name = pt.name.toLowerCase();
      if (r === name || r === String(pt.id) || name.includes(r) || (r.length >= 3 && r.includes(name))) {
        d.paymentTypeId = pt.id;
        return pt.name;
      }
    }

    const aliasToKeywords: Record<string, string[]> = {
      cash:      ['cash', '1', 'paid'],
      card:      ['card', '2', 'debit', 'credit'],
      upi:       ['upi', 'gpay', 'paytm', 'phonepe'],
      online:    ['online', '3', 'net', 'digital'],
      cheque:    ['cheque', 'check', 'chq', '4'],
      insurance: ['insurance', 'insur', 'tpa'],
    };

    const aliases = Object.keys(aliasToKeywords);
    for (let i = 0; i < aliases.length; i++) {
      const alias = aliases[i];
      const keys = aliasToKeywords[alias];
      if (keys.indexOf(r) >= 0 || r === alias) {
        for (const pt of this.paymentTypes) {
          const name = pt.name.toLowerCase();
          if (keys.some(k => name.includes(k)) || name.includes(alias)) {
            d.paymentTypeId = pt.id;
            return pt.name;
          }
        }
      }
    }

    const fallback: Record<string, [number, string]> = {
      cash: [1, 'Cash'], card: [2, 'Card'], online: [3, 'Online'],
      upi: [3, 'Online'], cheque: [4, 'Cheque'], insurance: [7, 'Insurance'],
      '1': [1, 'Cash'], '2': [2, 'Card'], '3': [3, 'Online'],
    };
    if (fallback[r]) {
      const [id, name] = fallback[r];
      d.paymentTypeId = id;
      return name;
    }
    return null;
  }

  private resetSession(): void {
    this.resetBackendSession();
    this.resetFlow();
  }

  private resetBackendSession(): void {
    const oldId = this.sessionId;
    this.http.post(`${this.BACKEND_URL.replace('/chat', '/chat/reset')}?session_id=${oldId}`, {}).subscribe({
      error: () => {}
    });
  }

  /** Reset billing flow state but keep chat history. */
  private resetFlow(): void {
    this.chatState            = 'idle';
    this.draft                = {};
    this.collectStep          = 'uhid';
    this.isTyping             = false;
    this.backendAvailable     = true;
    this.sessionId            = newSessionId();
    this.pendingServiceOptions.clear();
  }

  private isYes(m: string): boolean {
    return /^(yes|y|ok|okay|confirm|sure|proceed|yep|yeah|yup|haan|ha|correct|right|go|submit|done|save|1)$/i.test(m.trim());
  }
  private isNo(m: string): boolean {
    return /^(no|n|cancel|stop|nope|nah|nahi|na|wrong|incorrect|quit|exit|0)$/i.test(m.trim());
  }

  private addBotMessage(text: string, buttons?: BtnOption[]): void {
    this.isTyping = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.isTyping = false;
      this.pushMessage({ sender: 'bot', text, timestamp: new Date(), buttons: buttons || [] });
      this.scrollToBottom();
      this.cdr.detectChanges();
    }, 420);
  }

  goBack(): void {
    this.resetSession();
    this.shouldAutoScroll = true;
    this.scrollToBottom(true);
  }

  closeChat(): void {
    this.viewMode = 'closed';
    this.setBodyScrollLock(false);
    this.saveHistory();
    this.cdr.detectChanges();
  }

  /** Side panel — stays visible while billing page is active. */
  dockChat(): void {
    this.viewMode = 'docked';
    this.setBodyScrollLock(false);
    this.saveHistory();
    this.cdr.detectChanges();
    setTimeout(() => this.scrollToBottom(), 100);
  }

  expandChat(): void {
    this.viewMode = 'fullscreen';
    this.setBodyScrollLock(true);
    this.cdr.detectChanges();
    this.scrollToBottom();
    this.focusInput(200);
  }

  private _centerFloatingPanel(): void {
    this.panelWidth  = 420;
    this.panelHeight = Math.min(window.innerHeight - 40, 680);
    this.panelLeft   = Math.max(20, (window.innerWidth - this.panelWidth) / 2);
    this.panelTop    = Math.max(20, (window.innerHeight - this.panelHeight) / 2);
  }

  /** @deprecated use closeChat */
  minimizeChat(): void { this.closeChat(); }

  // Only focus the input if the user is NOT currently selecting text in a message bubble
  ensureFocus(event?: MouseEvent): void {
    const sel = window.getSelection();
    if (sel && sel.toString().length > 0) return;
    this.focusInput(50);
  }

  openChat(): void {
    try { (document.activeElement as any)?.blur(); } catch {}
    this.expandChat();
  }

  toggleChat(): void {
    if (this.viewMode === 'closed') {
      this.expandChat();
    } else {
      this.closeChat();
    }
  }

  private focusInput(delay = 50): void {
    setTimeout(() => { try { this.messageInput?.nativeElement?.focus(); } catch {} }, delay);
  }
}
